import { Suspense, lazy, useEffect, useMemo, useRef } from 'react';
import { createBrowserRouter, Outlet, RouterProvider, useNavigate, useRouteError, useLocation } from 'react-router-dom';
import { useLanguage } from '../providers/LanguageProvider';
import { SidebarProvider } from '../../context/SidebarContext';
import { SkipToMain } from '../../components/primitives';
import { Uqda } from '../../components/mascot/Uqda';
// Sidebar is lazy so the protected-only chunk (which drags in SubscriptionFlow
// → SubscriptionCheckoutModal) never lands in the public landing main bundle.
import { authProtectedRoutes, authPublicRoutes } from '../../features/auth/routes';
import { aiChatProtectedRoutes, aiChatPublicRoutes } from '../../features/aiChat/routes';
import { billingRoutes } from '../../features/billing/routes';
import { documentRoutes } from '../../features/documents/routes';
import { flashcardProtectedRoutes, flashcardPublicRoutes } from '../../features/flashcards/routes';
import { financeRoutes } from '../../features/finance/routes';
import { raskhRoutes } from '../../features/sessions/routes';
import {
  SidebarSkeleton,
  ChatLayoutSkeleton,
  DashboardSkeleton,
  TodaySkeleton,
  SettingsSkeleton,
  FlashcardsSkeleton,
  FinanceSkeleton,
  AuthFormSkeleton,
  PublicHomeSkeleton,
  PublicArticleSkeleton,
  PricingSkeleton,
  SharedReviewSkeleton,
  PublicChatSkeleton,
  RaskhHomeSkeleton,
  PlayerSkeleton,
  MindMapSkeleton,
  DocumentInsightsSkeleton,
  BillingCompleteSkeleton,
  ProtectedLayoutSkeleton,
  SkeletonBox,
  SkeletonText,
} from '../../components/skeletons';
import '../../components/skeletons/primitives/Skeletons.css';
import PageTransition from '../../styles/motion/PageTransition';

const ProtectedRoute = lazy(() => import('../../account/ProtectedRoute'));
const Sidebar = lazy(() => import('../../components/home/Sidebar'));
// Home is STATIC (not lazy). The landing's hero is React's mobile LCP
// element, and lazy-loading Home was costing ~2s of LCP delay because the
// chunk load triggered post-hydration reconciliation that Lighthouse
// counted as a new LCP candidate. Static-importing puts the hero in main
// — React manages it from initial mount, the LCP fires at FCP (~1.7s
// mobile). Home itself only carries Nav + HeroSection + PageHead schemas;
// everything below the hero lazy-loads via LandingBelowHero inside Home.
// eslint-disable-next-line import/first
import Home from '../../pages/HomePage/Home';
// StoreShell wraps the Redux Provider + PersistGate + AppBootstrapper. Lazy
// so the public landing tree (which renders zero Redux-connected components
// outside of the lazy NewChat demo) never pays the ~195 KB cost in the main
// bundle. Mounted around protected layout + every public route that uses
// Redux (auth, aiChat demos, shared flashcards/study bundles).
const StoreShell = lazy(() => import('../StoreShell'));
const withStore = (element) => <StoreShell>{element}</StoreShell>;
const PrivacyPolicy = lazy(() => import('../../pages/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('../../pages/Legal/TermsOfService'));
const About = lazy(() => import('../../pages/PublicPages/About'));
const Feature = lazy(() => import('../../pages/PublicPages/Feature'));
const Guides = lazy(() => import('../../pages/PublicPages/Guides'));
const Pricing = lazy(() => import('../../pages/Pricing/Pricing'));
const NotFound = lazy(() => import('../../pages/NotFound/NotFound'));
const SpeakTest = lazy(() => import('../../pages/SpeakTest/SpeakTest'));
// Dev-only mascot playground — gated below so the route only mounts in non-production builds.
const UqdaPlayground = lazy(() => import('../../pages/dev/UqdaPlayground'));

const visuallyHiddenStyle = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const LoadingFallback = ({ variant = 'public' }) => {
  const title = variant === 'protected' ? 'Loading your workspace' : 'Loading the page';
  const description =
    variant === 'protected'
      ? 'Verifying access and warming up your tools.'
      : 'Fetching content and preparing the page.';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="loading-fallback"
      style={{
        minHeight: '70vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--color-surface-subtle)',
        padding: '2rem',
      }}
    >
      <div
        style={{
          width: 'min(960px, 92vw)',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'clamp(1.25rem, 2vw, 2rem)',
          boxShadow: 'var(--shadow-xl)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
          <div
            aria-hidden="true"
            className="raskh-anim-spin"
            style={{
              width: '2.8rem',
              height: '2.8rem',
              borderRadius: '999px',
              border: '3px solid var(--color-border)',
              borderTopColor: 'var(--color-primary)',
              borderRightColor: 'var(--color-primary)',
            }}
          />
          <div>
            <p
              style={{
                margin: 0,
                color: 'var(--color-body)',
                fontWeight: 700,
                fontSize: '1.05rem',
                letterSpacing: '-0.01em',
              }}
            >
              {title}
            </p>
            <p style={{ margin: '0.2rem 0 0', color: 'var(--color-body-muted)', fontSize: '0.95rem' }}>{description}</p>
          </div>
        </div>

        <div
          aria-hidden="true"
          style={{
            display: 'grid',
            gap: '0.75rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          }}
        >
          {[1, 2, 3, 4].map((key) => (
            <div key={`card-${key}`} className="skeleton-base" style={{ height: '110px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-subtle)' }} />
          ))}
          <div className="skeleton-base" style={{ height: '14px', gridColumn: '1 / -1', marginTop: '0.2rem', borderRadius: 'var(--radius-sm)' }} />
          <div className="skeleton-base" style={{ height: '14px', width: '70%', gridColumn: '1 / -1', borderRadius: 'var(--radius-sm)' }} />
        </div>

        <span style={visuallyHiddenStyle}>
          {title}. {description}
        </span>
      </div>
    </div>
  );
};

// Route-to-skeleton mapping for PUBLIC routes (mirrors the protected resolver below).
// Each skeleton is self-contained — they own their nav/footer/article chrome where
// needed, so the resolver does not need a wrapper layout.
const PUBLIC_SKELETON_MAP = {
  '/': PublicHomeSkeleton,
  '/about': PublicArticleSkeleton,
  '/feature': PublicArticleSkeleton,
  '/guides': PublicArticleSkeleton,
  '/pricing': PricingSkeleton,
  '/privacy-policy': PublicArticleSkeleton,
  '/terms-of-service': PublicArticleSkeleton,
  // Arabic mirrors — same skeletons.
  '/ar': PublicHomeSkeleton,
  '/ar/': PublicHomeSkeleton,
  '/ar/about': PublicArticleSkeleton,
  '/ar/feature': PublicArticleSkeleton,
  '/ar/guides': PublicArticleSkeleton,
  '/ar/pricing': PricingSkeleton,
  '/ar/privacy-policy': PublicArticleSkeleton,
  '/ar/terms-of-service': PublicArticleSkeleton,
  '/login': AuthFormSkeleton,
  '/signup': AuthFormSkeleton,
  '/reset-password': AuthFormSkeleton,
  '/auth/start': AuthFormSkeleton,
  '/flashcards/shared': SharedReviewSkeleton,
  '/share/study-bundle': SharedReviewSkeleton,
  '/new-chat': PublicChatSkeleton,
  '/home/new-chat': PublicChatSkeleton,
};

const PUBLIC_SKELETON_PATTERNS = [
  { pattern: /^\/activate\//, skeleton: AuthFormSkeleton },
  { pattern: /^\/password\/reset\/confirm\//, skeleton: AuthFormSkeleton },
  { pattern: /^\/reset-password\//, skeleton: AuthFormSkeleton },
  { pattern: /^\/share\//, skeleton: SharedReviewSkeleton },
  { pattern: /^\/new-chat\//, skeleton: PublicChatSkeleton },
];

const getPublicSkeletonForRoute = (pathname) => {
  if (PUBLIC_SKELETON_MAP[pathname]) return PUBLIC_SKELETON_MAP[pathname];
  for (const { pattern, skeleton } of PUBLIC_SKELETON_PATTERNS) {
    if (pattern.test(pathname)) return skeleton;
  }
  return null;
};

const PublicRouteSkeleton = () => {
  const location = useLocation();
  const SkeletonComponent = getPublicSkeletonForRoute(location.pathname);

  return (
    <div role="status" aria-live="polite" aria-busy="true">
      {SkeletonComponent ? <SkeletonComponent /> : <LoadingFallback variant="public" />}
      <span style={visuallyHiddenStyle}>Loading the page...</span>
    </div>
  );
};

const publicFallback = <PublicRouteSkeleton />;

// Route-to-skeleton mapping for protected routes
const SKELETON_MAP = {
  // Chat/Study routes
  '/home/tools/chat-ai': ChatLayoutSkeleton,
  '/home/tools': ChatLayoutSkeleton,
  '/home/tools/study-session': ChatLayoutSkeleton,
  '/new-chat': ChatLayoutSkeleton,
  '/home/new-chat': ChatLayoutSkeleton,
  '/home/tools/generate-question': ChatLayoutSkeleton,
  '/home/tools/generate-card': ChatLayoutSkeleton,

  // Dashboard
  '/home/dashboard': DashboardSkeleton,

  // Due Today
  '/due-today': TodaySkeleton,

  // Settings
  '/account/settings': SettingsSkeleton,

  // Billing
  '/billing/complete': BillingCompleteSkeleton,

  // Raskh routes (vision-aligned)
  '/raskh': RaskhHomeSkeleton,
  '/raskh/session': RaskhHomeSkeleton,
  '/raskh/add-source': RaskhHomeSkeleton,

  // Speak test (audio recorder + player surface)
  '/speak-test': PlayerSkeleton,

  // Finance
  '/finance': FinanceSkeleton,

  // Flashcards
  '/flashcards': FlashcardsSkeleton,
  '/subject': FlashcardsSkeleton,

  // Usage
  '/usage': DashboardSkeleton,
};

// Pattern-based route matching (order matters — more specific patterns first)
const SKELETON_PATTERNS = [
  // Flashcard review routes (with section prefix)
  { pattern: /^\/subject\/.*\/topics\/.*\/section\/.*\/flashcards_review/, skeleton: FlashcardsSkeleton },
  { pattern: /^\/subject\/.*\/topics\/.*\/section\/.*\/hard-flashcards/, skeleton: FlashcardsSkeleton },
  // Flashcard review routes (without section prefix)
  { pattern: /^\/subject\/.*\/topics\/.*\/flashcards_review/, skeleton: FlashcardsSkeleton },
  { pattern: /^\/subject\/.*\/topics\/.*\/hard-flashcards/, skeleton: FlashcardsSkeleton },
  { pattern: /^\/subject\/.*\/topics\/.*\/flashcard_add/, skeleton: FlashcardsSkeleton },
  // Study session sub-routes → chat layout
  { pattern: /^\/home\/tools\/study-session\/sections/, skeleton: ChatLayoutSkeleton },
  // Section routes → chat layout
  { pattern: /^\/subject\/.*\/topics\/.*\/section/, skeleton: ChatLayoutSkeleton },
  // Topics list → flashcards
  { pattern: /^\/subject\/.*\/topics/, skeleton: FlashcardsSkeleton },
  // Due today sub-routes
  { pattern: /^\/due-today\/subject/, skeleton: FlashcardsSkeleton },
  // Documents — dedicated skeletons (replace generic ChatLayoutSkeleton)
  { pattern: /^\/documents\/.*\/mindmap/, skeleton: MindMapSkeleton },
  { pattern: /^\/documents\/.*\/insights/, skeleton: DocumentInsightsSkeleton },
  // Generate tools
  { pattern: /^\/home\/tools\/generate/, skeleton: ChatLayoutSkeleton },
  // New chat variants
  { pattern: /^\/new-chat/, skeleton: ChatLayoutSkeleton },
  // Raskh routes (any sub-route)
  { pattern: /^\/raskh/, skeleton: RaskhHomeSkeleton },
];

const getSkeletonForRoute = (pathname) => {
  // Check exact matches first
  if (SKELETON_MAP[pathname]) {
    return SKELETON_MAP[pathname];
  }

  // Check pattern matches
  for (const { pattern, skeleton } of SKELETON_PATTERNS) {
    if (pattern.test(pathname)) {
      return skeleton;
    }
  }

  // Default fallback
  return null;
};

// Inner protected skeleton — used by AppLayout's Suspense.
// The real Sidebar is rendered by AppLayout (sibling), so this skeleton
// must NOT render its own SidebarSkeleton — that would double up the sidebar.
const ProtectedRouteSkeleton = () => {
  const location = useLocation();
  const SkeletonComponent = getSkeletonForRoute(location.pathname);

  return (
    <div role="status" aria-live="polite" aria-busy="true" className="skeleton-main">
      {SkeletonComponent ? (
        <SkeletonComponent />
      ) : (
        // Default protected layout skeleton
        <div style={{ padding: 'var(--space-xl)' }}>
          <div style={{ marginBottom: 'var(--space-xl)' }}>
            <SkeletonText widths={['200px']} lineHeight="28px" />
            <SkeletonText widths={['300px']} lineHeight="16px" style={{ marginTop: 'var(--space-sm)' }} />
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-lg)', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {[1, 2, 3, 4].map((item) => (
              <SkeletonBox key={item} height={150} borderRadius="var(--radius-lg)" />
            ))}
          </div>
        </div>
      )}
      <span style={visuallyHiddenStyle}>Loading your workspace...</span>
    </div>
  );
};

const protectedFallback = <ProtectedRouteSkeleton />;

// Outer protected skeleton — used by the Suspense around ProtectedRoute itself.
// At this point AppLayout hasn't mounted yet so the real Sidebar isn't rendered,
// so we include SidebarSkeleton here to keep the full chrome present during the
// cold-start lazy-load of ProtectedRoute.
const outerProtectedFallback = (
  <div role="status" aria-live="polite" aria-busy="true">
    <SidebarSkeleton />
    <ProtectedRouteSkeleton />
  </div>
);

const chunkReloadStorageKey = 'app:chunk-reload';

const isChunkLoadError = (error) => {
  if (!error) return false;
  const message = (error?.message || '').toString();
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /dynamically imported module/i.test(message)
  );
};

const AppRouteError = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const chunkError = useMemo(() => isChunkLoadError(error), [error]);

  useEffect(() => {
    if (!chunkError || typeof window === 'undefined') return;
    try {
      const hasReloaded = window.sessionStorage.getItem(chunkReloadStorageKey) === '1';
      if (!hasReloaded) {
        window.sessionStorage.setItem(chunkReloadStorageKey, '1');
        window.location.reload();
      } else {
        window.sessionStorage.removeItem(chunkReloadStorageKey);
      }
    } catch (err) {
      // Best-effort reload; ignore storage issues.
    }
  }, [chunkError]);

  const handleReload = () => {
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(chunkReloadStorageKey);
      } catch (err) {
        // ignore storage reset errors
      }
      window.location.reload();
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleHome = () => navigate('/', { replace: true });

  const title = chunkError ? 'Just a sec — refreshing things' : 'That didn’t go as planned';
  const eyebrow = chunkError ? 'New version available' : 'Unexpected hiccup';
  const description = chunkError
    ? 'A newer version of the app is ready. Reloading brings it in — your progress is safe.'
    : 'Uqda took a wrong turn on the way to this page. Reload to try the same path, or head back home.';
  // chunk error reads as "found a new path" (curious + discovering);
  // a generic error reads as "puzzled" (thinking + idle).
  const mascotEmotion = chunkError ? 'curious' : 'thinking';
  const mascotPose = chunkError ? 'discovering' : 'idle';
  const stripeColor = chunkError ? 'var(--c-strategy)' : 'var(--c-ethics)';
  const eyebrowColor = chunkError ? 'var(--c-strategy)' : 'var(--c-ethics)';

  const sceneStyle = {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: 'clamp(1.5rem, 4vw, 3rem)',
    backgroundColor: 'var(--paper)',
    backgroundImage:
      'radial-gradient(circle at 18% 12%, color-mix(in srgb, var(--c-memory) 10%, transparent) 0%, transparent 42%),' +
      'radial-gradient(circle at 82% 88%, color-mix(in srgb, var(--c-strategy) 9%, transparent) 0%, transparent 48%)',
    backgroundAttachment: 'fixed',
    color: 'var(--ink)',
    fontFamily: 'var(--font-family-sans)',
  };

  const cardStyle = {
    position: 'relative',
    overflow: 'hidden',
    width: 'min(560px, 94vw)',
    background: 'color-mix(in srgb, var(--paper) 90%, #fff)',
    borderRadius: 'var(--radius-xl, 1.25rem)',
    padding: 'clamp(1.75rem, 3.5vw, 2.5rem)',
    border: '1px solid color-mix(in srgb, var(--ink) 12%, transparent)',
    boxShadow:
      '0 1px 2px color-mix(in srgb, var(--ink) 4%, transparent),' +
      '0 12px 32px color-mix(in srgb, var(--ink) 8%, transparent)',
    display: 'grid',
    justifyItems: 'center',
    textAlign: 'center',
    gap: 'var(--space-sm, 0.75rem)',
  };

  const stripeStyle = {
    position: 'absolute',
    insetBlockStart: 0,
    insetInlineStart: 0,
    insetInlineEnd: 0,
    height: '4px',
    background: stripeColor,
    boxShadow: `0 0 14px color-mix(in srgb, ${stripeColor} 50%, transparent)`,
  };

  const mascotStyle = {
    width: 130,
    height: 130,
    marginBlockEnd: 'var(--space-xs, 0.5rem)',
    transformOrigin: 'center',
  };

  const eyebrowStyle = {
    margin: 0,
    fontSize: '0.7rem',
    color: eyebrowColor,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '4px 10px',
    borderRadius: '999px',
    background: `color-mix(in srgb, ${eyebrowColor} 10%, transparent)`,
    border: `1px solid color-mix(in srgb, ${eyebrowColor} 25%, transparent)`,
  };

  const titleStyle = {
    margin: '0.5rem 0 0',
    fontSize: 'clamp(1.45rem, 2.4vw, 1.85rem)',
    fontWeight: 700,
    color: 'var(--ink)',
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
  };

  const descriptionStyle = {
    margin: '0.25rem 0 1.25rem',
    color: 'color-mix(in srgb, var(--ink) 65%, transparent)',
    lineHeight: 1.55,
    fontSize: '0.95rem',
    maxWidth: '38ch',
  };

  const chunkDetailsStyle = {
    margin: '0 0 1rem',
    fontSize: '0.8rem',
    color: 'color-mix(in srgb, var(--ink) 50%, transparent)',
    fontFamily: 'var(--font-family-mono, monospace)',
    padding: '6px 10px',
    background: 'color-mix(in srgb, var(--paper) 70%, color-mix(in srgb, var(--paper) 90%, #fff))',
    borderRadius: 'var(--radius-sm, 0.5rem)',
    border: '1px solid color-mix(in srgb, var(--ink) 8%, transparent)',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const actionsStyle = {
    display: 'flex',
    gap: '0.6rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
  };

  const primaryBtnStyle = {
    background: 'var(--emerald)',
    color: 'var(--paper)',
    border: 'none',
    padding: '0.65rem 1.4rem',
    borderRadius: 'var(--radius-pill, 999px)',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    boxShadow: '0 4px 12px color-mix(in srgb, var(--emerald) 28%, transparent)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    transition: 'transform 150ms ease, box-shadow 150ms ease, background 150ms ease',
  };

  const ghostBtnStyle = {
    background: 'transparent',
    color: 'color-mix(in srgb, var(--ink) 75%, transparent)',
    border: '1px solid color-mix(in srgb, var(--ink) 18%, transparent)',
    padding: '0.65rem 1.4rem',
    borderRadius: 'var(--radius-pill, 999px)',
    fontWeight: 600,
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'background 150ms ease, border-color 150ms ease',
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="app-route-error"
      style={sceneStyle}
    >
      <div style={cardStyle}>
        <span style={stripeStyle} aria-hidden="true" />
        <span aria-hidden="true" style={mascotStyle} className="app-route-error__mascot">
          <Uqda
            size={130}
            emotion={mascotEmotion}
            pose={mascotPose}
            connections={3}
            color="var(--emerald)"
            ink="var(--ink)"
            paper="var(--paper)"
            accent="var(--amber)"
          />
        </span>
        <span style={eyebrowStyle}>
          <span
            aria-hidden="true"
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: eyebrowColor,
              boxShadow: `0 0 0 4px color-mix(in srgb, ${eyebrowColor} 16%, transparent)`,
            }}
          />
          {eyebrow}
        </span>
        <h1 style={titleStyle}>{title}</h1>
        <p style={descriptionStyle}>{description}</p>
        {chunkError && error?.message ? (
          <p style={chunkDetailsStyle} title={error.message}>
            {error.message}
          </p>
        ) : null}
        <div style={actionsStyle}>
          <button
            type="button"
            onClick={handleReload}
            style={primaryBtnStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 18px color-mix(in srgb, var(--emerald) 38%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px color-mix(in srgb, var(--emerald) 28%, transparent)';
            }}
          >
            <span aria-hidden="true">{chunkError ? '↻' : '⟳'}</span>
            {chunkError ? 'Reload now' : 'Try again'}
          </button>
          <button
            type="button"
            onClick={handleHome}
            style={ghostBtnStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'color-mix(in srgb, var(--c-memory) 8%, transparent)';
              e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--c-memory) 30%, transparent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--ink) 18%, transparent)';
            }}
          >
            Go home
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * RouteAnchor — focusable wrapper that receives focus on route change so
 * screen-reader/keyboard users land at the top of the new page.
 *
 * Pages own their own <main id="main"> landmark; this anchor only marks
 * itself with data-route-anchor so SkipToMain can fall back to it when
 * a page doesn't declare an explicit main region.
 */
const RouteAnchor = ({ children }) => {
  const location = useLocation();
  const anchorRef = useRef(null);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const node = anchorRef.current;
    if (!node) return;
    if (document.activeElement && node.contains(document.activeElement)) return;
    // Prefer focusing the page's own <main id="main"> if one exists.
    const pageMain = node.querySelector('main#main, [data-page-main="true"]');
    const target = pageMain || node;
    if (!target.hasAttribute('tabindex') && target === node) {
      // Anchor has tabindex=-1 already; pageMain may not need one since
      // it's a landmark, but add if missing to allow focus.
    } else if (pageMain && !pageMain.hasAttribute('tabindex')) {
      pageMain.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: false });
  }, [location.pathname]);

  return (
    <div
      ref={anchorRef}
      tabIndex={-1}
      data-route-anchor="true"
      style={{ outline: 'none', minHeight: '100%' }}
    >
      {children}
    </div>
  );
};

const PublicLayout = () => (
  <>
    <SkipToMain />
    <RouteAnchor>
      <PageTransition>
        <Suspense fallback={publicFallback}>
          <Outlet />
        </Suspense>
      </PageTransition>
    </RouteAnchor>
  </>
);

const MascotToastHost = lazy(() => import('../../components/mascot/MascotToastHost'));
// LibrarySearch — the global Cmd/Ctrl-K command palette over the learner's
// whole library (T2.3). It owns its own window keydown listener, so it acts
// globally, but it consumes the RTK Query `useSearchChunksQuery` hook and must
// therefore live inside StoreShell. The protected AppLayout is the app-wide
// shell that always has the store + is always mounted for authenticated users,
// so it is the correct global mount point (App.jsx sits above StoreShell and
// has no Redux store on public/landing routes).
const LibrarySearch = lazy(() => import('../../components/search/LibrarySearch'));

// StoreShell is mounted ONE level up (around <ProtectedRoute />) so the route
// element itself can use Redux hooks. Wrapping it here too would create a
// duplicate Provider + AppBootstrapper.
const AppLayout = () => (
  <SidebarProvider>
    <SkipToMain />
    <Suspense fallback={<SidebarSkeleton />}>
      <Sidebar />
    </Suspense>
    <RouteAnchor>
      <PageTransition>
        <Suspense fallback={protectedFallback}>
          <Outlet />
        </Suspense>
      </PageTransition>
    </RouteAnchor>
    <Suspense fallback={null}>
      <MascotToastHost />
    </Suspense>
    <Suspense fallback={null}>
      <LibrarySearch />
    </Suspense>
  </SidebarProvider>
);

// LangBoundary — forces the LanguageProvider language for /ar/* routes so
// prerendered HTML, Helmet's <html lang/dir>, and i18next all resolve to the
// route's locale. setLanguage triggers LanguageProvider's effect which both
// calls i18n.changeLanguage and writes to document.documentElement.
const LangBoundary = ({ lang, children }) => {
  const { language, setLanguage } = useLanguage();
  useEffect(() => {
    if (language !== lang) setLanguage(lang);
  }, [lang, language, setLanguage]);
  return children;
};

const wrapAr = (element) => <LangBoundary lang="ar">{element}</LangBoundary>;

const publicAppRoutes = [
  { path: '/', element: <Home /> },
  { path: '/about', element: <About /> },
  { path: '/feature', element: <Feature /> },
  { path: '/guides', element: <Guides /> },
  { path: '/pricing', element: <Pricing /> },
  { path: '/privacy-policy', element: <PrivacyPolicy /> },
  { path: '/terms-of-service', element: <TermsOfService /> },
  // Dev-only mascot playground. The element is just <NotFound /> when built for production
  // so the route exists but produces no visible content.
  ...(process.env.NODE_ENV !== 'production'
    ? [{ path: '/dev/uqda', element: <UqdaPlayground /> }]
    : []),
  // Arabic-prefixed mirror routes for SEO / hreflang correctness.
  // Same components, language forced via LangBoundary on mount.
  { path: '/ar', element: wrapAr(<Home />) },
  { path: '/ar/', element: wrapAr(<Home />) },
  { path: '/ar/about', element: wrapAr(<About />) },
  { path: '/ar/feature', element: wrapAr(<Feature />) },
  { path: '/ar/guides', element: wrapAr(<Guides />) },
  { path: '/ar/pricing', element: wrapAr(<Pricing />) },
  { path: '/ar/privacy-policy', element: wrapAr(<PrivacyPolicy />) },
  { path: '/ar/terms-of-service', element: wrapAr(<TermsOfService />) },
];

const protectedAppRoutes = [{ path: '/speak-test', element: <SpeakTest /> }];

// publicAppRoutes (landing, about, feature, guides, pricing, legal) intentionally
// render without StoreShell — none of them use Redux at the page level. The only
// landing-tree Redux consumer is the lazy NewChat demo, which mounts its own
// StoreShell inside TryItLive.jsx. All other public routes (auth, aiChat demos,
// shared flashcards/bundles) DO use Redux, so we wrap their elements here.
const withStorePublic = (routes) =>
  routes.map((r) => ({ ...r, element: withStore(r.element) }));

const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <AppRouteError />,
    children: [
      ...publicAppRoutes,
      ...withStorePublic(authPublicRoutes),
      ...withStorePublic(aiChatPublicRoutes),
      ...withStorePublic(flashcardPublicRoutes),
      { path: '*', element: <NotFound /> },
    ],
  },
  {
    element: (
      <Suspense fallback={outerProtectedFallback}>
        <StoreShell>
          <ProtectedRoute />
        </StoreShell>
      </Suspense>
    ),
    errorElement: <AppRouteError />,
    children: [
      {
        element: <AppLayout />,
        children: [
          ...protectedAppRoutes,
          ...authProtectedRoutes,
          ...aiChatProtectedRoutes,
          ...flashcardProtectedRoutes,
          ...documentRoutes,
          ...billingRoutes,
          ...financeRoutes,
          ...raskhRoutes,
        ],
      },
    ],
  },
]);

const AppRoutes = () => <RouterProvider router={router} />;

export default AppRoutes;
